import { useEffect, useRef, useState } from 'react';

import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  DebenturesResumeData,
  DebentureTitleData,
} from 'src/types/operations/debentures';

import PanelTitlesDebentures from './PanelTitlesDebentures';
import { If } from '../../../../../components/If';
import { PanelLogin } from '../../../../../components/PanelLogin';
import { useAuthContext } from '../../../../../context/AuthContextProvider';

interface OperationDebenturesResumeTitlesProps {
  titles?: DebenturesResumeData['titles'];
}

function PaperTitles({
  typeTitle,
  data,
  isExtraSmall,
}: {
  typeTitle: string;
  data?: DebentureTitleData[];
  isExtraSmall: boolean;
}) {
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);
  const [count, setCount] = useState(1);
  const [percent, setPercent] = useState(0);
  const [slider, setSlider] = useState<{ width: number; length: number }>(
    {} as { width: number; length: number },
  );
  const dividend = isExtraSmall ? 80 : 100;

  function handleNextClick() {
    if (count < slider.length) {
      const percentage = dividend / slider.length;
      const translate = `translate(-${percentage * count}%)`;
      setPercent(percentage * count);
      setCount(count + 1);
      if (sliderRef.current) {
        sliderRef.current.style.transform = translate;
      }
    }
  }

  function handlePrevClick() {
    if (count <= slider.length) {
      const percentage = dividend / slider.length;
      const translate = `translate(-${percent - percentage}%)`;
      setPercent(percent - percentage);
      setCount(count - 1);
      if (sliderRef.current) {
        sliderRef.current.style.transform = translate;
      }
    }
  }

  useEffect(() => {
    const width = (data?.length ?? 0) * 100;
    const length = data?.length ?? 0;

    setSlider({ width, length });
  }, [data?.length]);

  return (
    <Paper
      square
      sx={(theme) => ({
        position: 'relative',
        height: 360,
        marginTop: theme.spacing(2),
        overflow: 'hidden',
        [theme.breakpoints.down('xs')]: {
          height: 400,
        },
      })}>
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.secondary.dark,
          width: 200,
          height: 360,
          position: 'absolute',
          padding: theme.spacing(3),
          [theme.breakpoints.down('xs')]: {
            height: 400,
          },
        })}>
        <Typography
          sx={(theme) => ({
            fontWeight: theme.typography.fontWeightBold,
            color: theme.palette.primary.main,
          })}
          variant="body1">
          &mdash;{typeTitle}
        </Typography>
      </Box>
      <Box
        sx={(theme) => ({
          margin: '50px 24px',
          width: 369,
          height: 265,
          position: 'absolute',
          [theme.breakpoints.down('xs')]: {
            margin: '70px 15px',
          },
        })}>
        <Box display="flex" width="100%" height="100%">
          <Box
            display="flex"
            height="100%"
            width={slider.width + '%'}
            flexShrink={0}
            ref={sliderRef}
            sx={{ transition: 'all 0.5s' }}>
            {data?.map(
              ({
                series,
                emissionDate,
                isin,
                quantityIssued,
                unitValue,
                remuneration,
              }) => (
                <PanelTitlesDebentures
                  key={`${isin}${series}`}
                  series={series}
                  emissionDate={emissionDate}
                  isin={isin}
                  quantityIssued={quantityIssued}
                  unitValue={unitValue}
                  remuneration={remuneration}
                />
              ),
            )}
          </Box>
        </Box>
      </Box>
      <Box
        right={0}
        bottom={0}
        display="flex"
        justifyContent="flex-end"
        position="absolute"
        p={1}>
        <Button
          ref={prevRef}
          onClick={handlePrevClick}
          color="primary"
          sx={{ border: '1px solid', height: 30, minWidth: 30 }}
          disabled={count === 1}>
          <KeyboardArrowLeft titleAccess="seta para esquerda" />
        </Button>
        <Button
          ref={nextRef}
          onClick={handleNextClick}
          color="primary"
          sx={{ border: '1px solid', height: 30, minWidth: 30 }}
          disabled={count === data?.length}>
          <KeyboardArrowRight titleAccess="seta para direita" />
        </Button>
      </Box>
    </Paper>
  );
}

export default function OperationDebenturesResumeTitles({
  titles,
}: OperationDebenturesResumeTitlesProps) {
  const { auth } = useAuthContext();
  const isExtraSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('xs'),
  );

  const canViewTitles =
    auth?.user?.subscription?.plan?.permissions
      ?.operacoes_debentures_resumo_titulos;

  return (
    <Box p={1}>
      <Box display="flex" alignItems="center">
        <Typography
          variant="subtitle2"
          sx={(theme) => ({
            fontWeight: theme.typography.fontWeightBold,
            fontSize: theme.typography.pxToRem(20),
          })}>
          &mdash;Títulos
        </Typography>
      </Box>
      <Divider
        sx={(theme) => ({
          height: 2,
          backgroundColor: theme.palette.common.black,
        })}
      />
      <If condition={!canViewTitles}>
        <Box py={1}>
          <PanelLogin message={null} small={null} hasLink={null} />
        </Box>
      </If>
      <If condition={Boolean(canViewTitles)}>
        {Boolean(titles?.seniors?.length) && (
          <PaperTitles
            typeTitle="Seniores"
            data={titles?.seniors}
            isExtraSmall={isExtraSmall}
          />
        )}
        {Boolean(titles?.mezzanines?.length) && (
          <PaperTitles
            typeTitle="Mezaninos"
            data={titles?.mezzanines}
            isExtraSmall={isExtraSmall}
          />
        )}
        {Boolean(titles?.subordinates?.length) && (
          <PaperTitles
            typeTitle="Subordinadas"
            data={titles?.subordinates}
            isExtraSmall={isExtraSmall}
          />
        )}
        {Boolean(titles?.uniques?.length) && (
          <PaperTitles
            typeTitle="Únicos"
            data={titles?.uniques}
            isExtraSmall={isExtraSmall}
          />
        )}
      </If>
    </Box>
  );
}
